import axios, { AxiosError } from 'axios';

const TOKEN_KEY = 'amuzze_admin_token';
const REFRESH_KEY = 'amuzze_admin_refresh';
const POST_LOGIN_PATH_KEY = 'amuzze_admin_post_login_path';

// Em prod (Cloudflare Pages), VITE_API_URL=https://api.amuzze.com.br/api/v1.
// Em dev, fica vazia → cai no fallback '/api/v1' e o proxy do Vite resolve
// pra http://localhost:3000.
export const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

export const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Single-flight refresh — evita race quando múltiplas requests batem 401
// simultâneas. O backend rotaciona o refresh_token a cada uso (revoga o
// antigo), então N refreshes paralelos = N-1 falhas → logout indesejado.
// Uma promise compartilhada garante 1 refresh só, os demais aguardam.
let refreshPromise: Promise<string> | null = null;

async function performRefresh(): Promise<string> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const refresh = localStorage.getItem(REFRESH_KEY);
      if (!refresh) throw new Error('No refresh token');
      const { data } = await axios.post(`${API_BASE}/auth/refresh`, { refreshToken: refresh });
      // Backend responde { success, data: { tokens: { accessToken, refreshToken } } }
      // NÃO usar data.data.accessToken direto — precisa passar por .tokens
      const newToken   = data?.data?.tokens?.accessToken;
      const newRefresh = data?.data?.tokens?.refreshToken;
      if (!newToken) throw new Error('Invalid refresh response');
      localStorage.setItem(TOKEN_KEY, newToken);
      if (newRefresh) localStorage.setItem(REFRESH_KEY, newRefresh);
      return newToken;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

function goToLoginPreservingPath() {
  const current = window.location.pathname + window.location.search;
  if (current && current !== '/login' && !current.startsWith('/login')) {
    sessionStorage.setItem(POST_LOGIN_PATH_KEY, current);
  }
  window.location.href = '/login';
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError<any>) => {
    const original: any = error.config;
    const code = error.response?.data?.error?.code;
    const status = error.response?.status;

    if (
      status === 401 &&
      ['TOKEN_EXPIRED', 'TOKEN_INVALID', 'TOKEN_REVOKED'].includes(code) &&
      !original._retry
    ) {
      original._retry = true;
      try {
        const newToken = await performRefresh();
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch {
        clearAuth();
        goToLoginPreservingPath();
      }
    }
    return Promise.reject(error);
  }
);

export function setAuth(accessToken: string, refreshToken: string) {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_KEY, refreshToken);
}

export function clearAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

// Consumido por useAuth após login bem-sucedido pra restaurar a página
// que o usuário estava antes da sessão expirar. Retorna null se não há path
// preservado (login direto pela raiz).
export function consumePostLoginPath(): string | null {
  const path = sessionStorage.getItem(POST_LOGIN_PATH_KEY);
  if (path) sessionStorage.removeItem(POST_LOGIN_PATH_KEY);
  return path;
}
